import { type ApiClientResponse, apiClient, restApiClient } from "@lobby/api-client";
import { useIntlT } from "@lobby/ocb-intl";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";

import { useMobile } from "../../shared";

export type Banner = ApiClientResponse<"Slider.getSimplified">["result"]["banners"][number];

interface BannerQueryResult {
  banners: Banner[];
}

export const Lobby = {
  useLobbySettings() {
    return useSuspenseQuery({
      queryKey: ["Lobby.settings"],
      queryFn: () =>
        restApiClient.send({
          endpoint: "GET /api/v1/settings",
          query: {
            language: "en",
          },
        }),
    });
  },

  /**
   * @deprecated Use useLobbySettings instead.
   */
  useLobby() {
    return useQuery({
      queryKey: ["Lobby.getCurrent"],
      queryFn: () =>
        apiClient.send({
          endpoint: "Lobby.getCurrent",
        }),
      select: (data) => data.result,
    });
  },

  useBannerDesktop() {
    const {
      intl: { locale, defaultLocale },
    } = useIntlT();

    return useQuery({
      queryKey: ["Slider.getSimplified", locale || defaultLocale, "desktop"],
      queryFn: () =>
        apiClient.send({
          endpoint: "Slider.getSimplified",
          params: {
            name: "test",
            lang: locale || defaultLocale,
            deviceType: "desktop",
          },
        }),
      select: (data) => data.result as BannerQueryResult,
    });
  },

  useBannerMobile() {
    const {
      intl: { locale, defaultLocale },
    } = useIntlT();

    return useQuery({
      queryKey: ["Slider.getSimplified", locale || defaultLocale, "mobile"],
      queryFn: () =>
        apiClient.send({
          endpoint: "Slider.getSimplified",
          params: {
            name: "test",
            lang: locale || defaultLocale,
            deviceType: "mobile",
          },
        }),
      select: (data) => data.result as BannerQueryResult,
    });
  },

  useBanner() {
    const {
      intl: { locale, defaultLocale },
    } = useIntlT();
    const isMobile = useMobile();
    const deviceType = isMobile ? "mobile" : "desktop";

    return useQuery({
      queryKey: ["Slider.getSimplified", locale || defaultLocale, deviceType],
      queryFn: () =>
        apiClient.send({
          endpoint: "Slider.getSimplified",
          params: {
            name: "test",
            lang: locale || defaultLocale,
            deviceType,
          },
        }),
      select: (data) => data.result,
    });
  },
};
